import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon, Plus, Trash2 } from "lucide-react";
import { InquiryHotelItem } from "@/types/database";

interface InquiryHotelFormProps {
  hotels: InquiryHotelItem[];
  onChange: (hotels: InquiryHotelItem[]) => void;
  nights: number;
}

export function InquiryHotelForm({ hotels, onChange, nights }: InquiryHotelFormProps) {
  const addHotel = () => {
    onChange([
      ...hotels,
      {
        hotel_name: "",
        checkin_date: format(new Date(), "yyyy-MM-dd"),
        checkout_date: format(new Date(), "yyyy-MM-dd"),
        room_rate: 0,
        nights: 1,
        room_count: 1,
        total_price: 0
      },
    ]);
  };

  const removeHotel = (index: number) => {
    const newHotels = [...hotels];
    newHotels.splice(index, 1);
    onChange(newHotels);
  };

  const updateHotel = (index: number, field: keyof InquiryHotelItem, value: any) => {
    const newHotels = [...hotels];
    newHotels[index] = {
      ...newHotels[index],
      [field]: value,
    };

    // Update nights when dates change
    if (field === 'checkin_date' || field === 'checkout_date') {
      const checkin = new Date(field === 'checkin_date' ? value : newHotels[index].checkin_date);
      const checkout = new Date(field === 'checkout_date' ? value : newHotels[index].checkout_date);
      
      // Calculate nights only if both dates are valid
      if (checkin && checkout && !isNaN(checkin.getTime()) && !isNaN(checkout.getTime())) {
        const diffTime = Math.abs(checkout.getTime() - checkin.getTime());
        const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        newHotels[index].nights = Math.max(1, nights); // Ensure minimum 1 night
      }
    }

    // Calculate total price
    if (field !== 'total_price') {
      newHotels[index].total_price = 
        (newHotels[index].room_rate || 0) * 
        (newHotels[index].nights || 0) * 
        (newHotels[index].room_count || 0);
    }

    onChange(newHotels);
  };

  const formatIDR = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-4">
      {hotels.map((hotel, index) => (
        <Card key={index}>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base">Hotel {index + 1}</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              onClick={() => removeHotel(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Hotel Name</Label>
                <Input
                  className="h-8 text-sm"
                  value={hotel.hotel_name}
                  onChange={(e) => updateHotel(index, 'hotel_name', e.target.value)}
                  placeholder="Enter hotel name"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Check-in</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal h-8 text-sm",
                        !hotel.checkin_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-1 h-3 w-3" />
                      {hotel.checkin_date ? (
                        format(new Date(hotel.checkin_date), "dd/MM/yy")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={new Date(hotel.checkin_date)}
                      onSelect={(date) =>
                        updateHotel(
                          index,
                          'checkin_date',
                          date ? format(date, "yyyy-MM-dd") : hotel.checkin_date
                        )
                      }
                      disabled={(date) =>
                        date < new Date() || date < new Date("1900-01-01")
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Check-out</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal h-8 text-sm",
                        !hotel.checkout_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-1 h-3 w-3" />
                      {hotel.checkout_date ? (
                        format(new Date(hotel.checkout_date), "dd/MM/yy")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={new Date(hotel.checkout_date)}
                      onSelect={(date) =>
                        updateHotel(
                          index,
                          'checkout_date',
                          date ? format(date, "yyyy-MM-dd") : hotel.checkout_date
                        )
                      }
                      disabled={(date) =>
                        date < new Date(hotel.checkin_date) || date < new Date("1900-01-01")
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Night</Label>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  value={hotel.nights}
                  readOnly
                  tabIndex={-1}
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Room Rate</Label>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  value={hotel.room_rate}
                  onChange={(e) => updateHotel(index, 'room_rate', parseFloat(e.target.value) || 0)}
                  placeholder="Enter room rate"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Room Count</Label>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  value={hotel.room_count}
                  onChange={(e) => updateHotel(index, 'room_count', parseInt(e.target.value) || 1)}
                  min={1}
                  placeholder="Number of rooms"
                />
              </div>

              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Total Price</Label>
                <Input
                  type="text"
                  className="h-8 text-sm"
                  value={formatIDR(hotel.total_price || 0)}
                  onChange={(e) => {
                    const numericValue = parseFloat(e.target.value.replace(/[^0-9]/g, '')) || 0;
                    updateHotel(index, 'total_price', numericValue);
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={addHotel}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add Hotel
      </Button>
    </div>
  );
}
